/**
 * Hook for the Tickets-sidebar unassigned-count badge.
 *
 * Wraps GET /api/v1/agent/tickets/unassigned-count which returns the
 * number of unassigned, non-spam, non-closed CLIENT tickets created
 * within the configured window (5 days at the time of writing).
 * Internal-work types (Platform Development, DevOps Task) are
 * excluded server-side (2026-06-11) - the badge is a "clients are
 * waiting" signal, not an internal backlog meter.
 *
 * The badge sits next to the "Tickets" nav item so an agent on the
 * "Mine" filter still notices fresh tickets that need someone to
 * pick them up. Polls every 30s (same cadence as the inbox-count
 * sidebar query).
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import api from "@/lib/api";
import type { UnassignedCountResponse } from "@/types/api";

export const UNASSIGNED_TICKET_COUNT_QUERY_KEY = [
  "agent",
  "tickets",
  "unassigned-count",
] as const;

export function useUnassignedTicketCount(): UseQueryResult<UnassignedCountResponse> {
  return useQuery({
    queryKey: UNASSIGNED_TICKET_COUNT_QUERY_KEY,
    queryFn: async () => {
      const response = await api.get<UnassignedCountResponse>(
        "/agent/tickets/unassigned-count",
      );
      return response.data;
    },
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}
